import { Box, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

interface UserPresenceChipProps {
  lastSeen: string | null;
  showTime?: boolean;
}

const UserPresenceChip: React.FC<UserPresenceChipProps> = ({ lastSeen, showTime = true }) => {
  const { t } = useTranslation();

  const getPresenceStatus = () => {
    if (!lastSeen) return { label: t('ADMIN_USER_OFFLINE'), color: '#9e9e9e', icon: '⚫' };
    
    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffInMinutes = (now.getTime() - lastSeenDate.getTime()) / (1000 * 60);
    
    if (diffInMinutes <= 5) {
      return { label: t('ADMIN_USER_ONLINE'), color: '#4caf50', icon: '🟢' };
    } else if (diffInMinutes <= 15) {
      return { label: t('ADMIN_USER_AWAY'), color: '#ff9800', icon: '🟡' };
    } else {
      return { label: t('ADMIN_USER_OFFLINE'), color: '#9e9e9e', icon: '⚫' };
    }
  };

  const presenceStatus = getPresenceStatus();
  const lastSeenText = lastSeen 
    ? formatDistanceToNow(new Date(lastSeen), { addSuffix: true, locale: enGB })
    : t('ADMIN_USER_NEVER');

  return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            padding: '4px 8px',
            borderRadius: '16px',
            backgroundColor: `${presenceStatus.color}20`,
            color: presenceStatus.color,
            border: `1px solid ${presenceStatus.color}`,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: `${presenceStatus.color}30`,
              transform: 'scale(1.02)',
            },
          }}
        >
          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
            {presenceStatus.icon} {presenceStatus.label}
          </Typography>
        </Box>
        {showTime && (
          <Typography 
            variant="caption" 
            color="textSecondary"
            dir={"ltr"}
            sx={{ 
              fontSize: '0.7rem',
              opacity: 0.8,
              fontStyle: 'italic'
            }}
          >
            {lastSeenText}
          </Typography>
        )}
      </Box>
  );
};

export default UserPresenceChip; 